export type AnalyticsEvent = {
  name: string;
  path: string;
  timestamp: string;
  properties?: Record<string, string | number | boolean>;
};

const queueKey = 'gcw.analytics.queue.v1';
const sessionKey = 'gcw.analytics.session.v1';

function safeStorage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[kind] ?? null;
  } catch {
    return null;
  }
}

function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the non-cryptographic preview-safe identifier.
  }
  return `gcw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId(): string {
  const store = safeStorage('sessionStorage');
  if (!store) return createSessionId();
  const existing = store.getItem(sessionKey);
  if (existing) return existing;
  const next = createSessionId();
  store.setItem(sessionKey, next);
  return next;
}

function safeReferrer(): string {
  try {
    return document.referrer ? new URL(document.referrer).hostname : 'direct';
  } catch {
    return 'unknown';
  }
}

export function track(name: string, properties: AnalyticsEvent['properties'] = {}) {
  try {
    if (typeof window === 'undefined') return;
    const event: AnalyticsEvent = {
      name,
      path: window.location.pathname + window.location.hash,
      timestamp: new Date().toISOString(),
      properties: {
        session: getSessionId(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        referrer: safeReferrer(),
        ...properties
      }
    };
    const endpoint = import.meta.env.VITE_GCW_ANALYTICS_ENDPOINT as string | undefined;
    if (endpoint && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
    const store = safeStorage('localStorage');
    if (!store) return;
    const current = loadAnalyticsQueue();
    store.setItem(queueKey, JSON.stringify([event, ...current].slice(0, 500)));
  } catch {
    // Analytics must never affect the website experience.
  }
}

export function loadAnalyticsQueue(): AnalyticsEvent[] {
  try {
    const store = safeStorage('localStorage');
    if (!store) return [];
    const raw = store.getItem(queueKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
